/**
 * ProfileReport.jsx
 *
 * Container responsável por:
 * - Controlar filtros
 * - Controlar paginação
 * - Disparar actions Redux
 * - Orquestrar exportação CSV
 *
 * NÃO faz:
 * - filtro client-side
 * - paginação client-side
 * - ordenação local (exceto roles para UI)
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { listRoles } from '@plone/volto/actions/roles/roles';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import {
  getProfileReport,
  getProfileReportCsv,
} from 'govbr.frontend.policy/actions/profile_report/profile_report';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import ProfileReportFilters from './ProfileReportFilters';
import ProfileReportTable from './ProfileReportTable';

import backSVG from '@plone/volto/icons/back.svg';

const messages = defineMessages({
  title: {
    id: 'Generate Profile Report',
    defaultMessage: 'Generate Profile Report',
  },
  profileReport: {
    id: 'Profile Report',
    defaultMessage: 'Profile Report',
  },
});

const ProfileReport = () => {
  const intl = useIntl();
  const location = useLocation();
  const dispatch = useDispatch();

  // -----------------------------
  // Estado local (UI)
  // -----------------------------
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [filtersOnFiltered, setFiltersOnFiltered] = useState([]);
  const [filtered, setFiltered] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [shouldExportCsv, setShouldExportCsv] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const exportTokenRef = useRef(null);

  // -----------------------------
  // Estado Redux
  // -----------------------------
  const {
    items = [],
    total = 0,
    page = 1,
    page_size = 30,
    loading = false,
  } = useSelector((state) => state.profile_report, shallowEqual);

  const { items: itemsCsv = [] } = useSelector(
    (state) => state.profile_report_csv,
    shallowEqual,
  );

  const { roles: rolesState = [] } = useSelector(
    (state) => state.roles,
    shallowEqual,
  );

  // -----------------------------
  // Roles (UI only)
  // -----------------------------
  const excludedRoleIds = useMemo(() => new Set(['Manager']), []);

  const roles = useMemo(() => {
    return rolesState
      .filter((role) => !excludedRoleIds.has(role.id))
      .slice() // garante imutabilidade antes do sort
      .sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'));
  }, [rolesState, excludedRoleIds]);

  // -----------------------------
  // Lifecycle
  // -----------------------------
  useEffect(() => {
    setIsClient(true);
    dispatch(listRoles());
  }, [dispatch]);

  // -----------------------------
  // Actions
  // -----------------------------
  const fetchReport = useCallback(
    ({
      page: newPage = page,
      pageSize = page_size,
      roles = selectedRoles,
    } = {}) => {
      setFiltered(true);
      setFiltersOnFiltered(roles);

      dispatch(
        getProfileReport(newPage, pageSize, roles.includes('all') ? [] : roles),
      );
    },
    [dispatch, page, page_size, selectedRoles],
  );

  const fetchReportCsv = useCallback(
    (roles) => {
      dispatch(getProfileReportCsv(roles.includes('all') ? [] : roles));
    },
    [dispatch],
  );

  const handleClear = useCallback(() => {
    setSelectedRoles([]);
    setFiltered(false);
  }, []);

  const handleExportCsv = useCallback(() => {
    if (isExporting) return;

    exportTokenRef.current = crypto.randomUUID();
    setIsExporting(true);
    setShouldExportCsv(true);

    fetchReportCsv(filtersOnFiltered);
  }, [fetchReportCsv, filtersOnFiltered, isExporting]);

  // -----------------------------
  // CSV Export Effect
  // -----------------------------
  useEffect(() => {
    if (!shouldExportCsv || !itemsCsv.length) return;

    try {
      const nowLabel = new Date().toLocaleString('pt-BR');
      const safeTimestamp = new Date().toISOString().replace(/[:.]/g, '-');

      const roleIdToTitleMap = new Map(
        rolesState.map((role) => [role.id, role.title]),
      );

      const appliedRoles =
        !filtersOnFiltered.length || filtersOnFiltered.includes('all')
          ? 'Todos'
          : filtersOnFiltered
              .map((roleId) => roleIdToTitleMap.get(roleId) || roleId)
              .join(' | ');

      const infoLines = [
        ['Portal Gov.BR'],
        ['Relatório de Perfis'],
        ['Data de Emissão', nowLabel],
        ['Filtros Utilizados', appliedRoles],
        ['Total', String(itemsCsv.length)],
        [],
      ];

      const headers = ['Nome do Usuário', 'CPF', 'E-mail', 'Perfis'];

      const rows = itemsCsv.map((user) => [
        user.name || '',
        user.cpf || '',
        user.email || '',
        (user.roles || []).map((r) => r.title || r.id).join(' | '),
      ]);

      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

      const csvContent =
        '\uFEFF' +
        [
          ...infoLines.map((l) => l.join(',')),
          headers.join(','),
          ...rows.map((r) => r.map(escape).join(',')),
        ].join('\r\n');

      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `relatorio_perfis_${safeTimestamp}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
      setShouldExportCsv(false);
      exportTokenRef.current = null;
    }
  }, [itemsCsv, shouldExportCsv, filtersOnFiltered, rolesState]);

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div className="ui container">
      <Helmet title={intl.formatMessage(messages.title)} />

      <h1 className="documentFirstHeading">
        {intl.formatMessage(messages.title)}
      </h1>

      <ProfileReportFilters
        roles={roles}
        selectedRoles={selectedRoles}
        onChangeRoles={setSelectedRoles}
        onGenerate={() =>
          fetchReport({
            page: 1,
            pageSize: filtersOnFiltered.length === 0 ? 30 : page_size,
          })
        }
        onClear={handleClear}
        onListAll={() => {
          setSelectedRoles(['all']);
          fetchReport({ page: 1, roles: [], pageSize: 'all' });
        }}
      />

      <ProfileReportTable
        items={items}
        total={total}
        currentPage={page}
        pageSize={page_size}
        loading={loading}
        filtered={filtered}
        onPageChange={(newPage) =>
          fetchReport({ page: newPage, pageSize: page_size })
        }
        onPageSizeChange={(newSize) =>
          fetchReport({ page: 1, pageSize: newSize })
        }
        onExportCsv={handleExportCsv}
        isExporting={isExporting}
        intl={intl}
      />

      {isClient &&
        createPortal(
          <Toolbar
            pathname={location.pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/" className="item">
                <Icon name={backSVG} size="30px" />
              </Link>
            }
          />,
          document.getElementById('toolbar'),
        )}
    </div>
  );
};

export default ProfileReport;
